import { Inject, Injectable } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { CaasMetaFormData } from '../../../hdi-components/src/shared/interfaces/caas/caas.interface';
import { BehaviorSubject } from 'rxjs';
import { CaasRoutesService, MetaData, SearchCategory } from 'hdi-components';
import { Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';

@Injectable({
  providedIn: 'root',
})
export class MetaDataService {
  private readonly metaData: BehaviorSubject<MetaData> =
    new BehaviorSubject<MetaData>({});
  constructor(
    private readonly meta: Meta,
    private readonly title: Title,
    private readonly router: Router,
    @Inject(CaasRoutesService)
    private readonly caasRoutesService: CaasRoutesService,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  clearMetaData() {
    this.metaData.next({});
    this.title.setTitle('HDI Bancassurance');
    // Remove all meta tags
    const metaTags = this.document.querySelectorAll('meta');
    metaTags.forEach((tag) => tag.parentNode?.removeChild(tag));

    // Remove the canonical link tag if it exists
    const canonicalLink = this.document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.parentNode?.removeChild(canonicalLink);
    }
  }

  /**
   * Set the meta data for the current page
   * @param metaData - The meta data to set
   */
  setMetaData(translatedMetaData: MetaData) {
    this.clearMetaData();

    if (translatedMetaData?.title) {
      this.title.setTitle(translatedMetaData.title);
      this.meta.updateTag({
        property: 'search:title',
        content: translatedMetaData.title,
      });
    }
    if (translatedMetaData?.description) {
      this.meta.updateTag({
        name: 'description',
        content: translatedMetaData.description,
      });
    }
    if (translatedMetaData?.keywords) {
      this.meta.updateTag({
        name: 'keywords',
        content: translatedMetaData.keywords,
      });
    }
    if (translatedMetaData?.robots) {
      this.meta.updateTag({
        name: 'robots',
        content: translatedMetaData.robots,
      });
    }
    if (translatedMetaData?.canonical) {
      let link: HTMLLinkElement = this.document.querySelector(
        'link[rel="canonical"]'
      ) as HTMLLinkElement;
      if (!link) {
        link = this.document.createElement('link');
        link.setAttribute('rel', 'canonical');
        this.document.head.appendChild(link);
      }
      link.setAttribute('href', translatedMetaData.canonical);
    }
    if (translatedMetaData?.openGraph?.title) {
      this.meta.updateTag({
        property: 'og:title',
        content: translatedMetaData.openGraph.title,
      });
    }
    if (translatedMetaData?.openGraph?.description) {
      this.meta.updateTag({
        property: 'og:description',
        content: translatedMetaData.openGraph.description,
      });
    }
    if (translatedMetaData?.openGraph?.image) {
      this.meta.updateTag({
        property: 'og:image',
        content: translatedMetaData.openGraph.image,
      });
    }
    if (translatedMetaData?.search?.category) {
      this.meta.updateTag({
        property: 'search:category',
        content: translatedMetaData.search.category,
      });
    }
  }

  /**
   * Translate the meta data from the CaasMetaFormData to the MetaData interface
   * @param metaData - The meta data to translate
   * @returns The translated meta data
   */
  translateMetaData(
    metaData: CaasMetaFormData,
    searchCategories?: SearchCategory[]
  ): MetaData {
    let translatedMetaData: MetaData = {};

    if (metaData?.md_title?.value) {
      translatedMetaData = {
        ...translatedMetaData,
        title: metaData.md_title.value,
      };
      translatedMetaData = {
        ...translatedMetaData,
        search: {
          title: metaData.md_title.value,
        },
      };
    }
    if (metaData?.md_description?.value) {
      translatedMetaData = {
        ...translatedMetaData,
        description: metaData.md_description.value,
      };
    }
    if (metaData?.md_keywords?.value) {
      translatedMetaData = {
        ...translatedMetaData,
        keywords: metaData.md_keywords.value,
      };
    }
    if (metaData?.md_robots?.value?.label) {
      translatedMetaData = {
        ...translatedMetaData,
        robots: metaData.md_robots.value.label,
      };
    }
    if (metaData?.md_canonical?.value?.identifier) {
      translatedMetaData = {
        ...translatedMetaData,
        canonical: this.getCanonicalRoute(
          metaData.md_canonical.value.identifier
        ),
      };
    }
    if (metaData?.md_og_title?.value) {
      translatedMetaData = {
        ...translatedMetaData,
        openGraph: {
          title: metaData.md_og_title.value,
        },
      };
    }
    if (metaData?.md_og_description?.value) {
      translatedMetaData = {
        ...translatedMetaData,
        openGraph: {
          ...translatedMetaData.openGraph,
          description: metaData.md_og_description.value,
        },
      };
    }
    if (metaData?.md_og_image?.value) {
      translatedMetaData = {
        ...translatedMetaData,
        openGraph: {
          ...translatedMetaData.openGraph,
          image: metaData.md_og_image.value.url,
        },
      };
    }
    if (metaData?.md_search_disable?.value) {
      translatedMetaData = {
        ...translatedMetaData,
        searchDisable: false,
      };
    }
    if (
      metaData?.pt_page_type_group?.value?.target?.identifier &&
      searchCategories
    ) {
      const searchCategory = searchCategories.find(
        (category) =>
          category.identifier ===
          metaData?.pt_page_type_group?.value?.target?.identifier
      );
      if (searchCategory) {
        translatedMetaData = {
          ...translatedMetaData,
          search: {
            category: searchCategory.category,
          },
        };
      }
    }
    return translatedMetaData;
  }

  /**
   * Get the canonical route from the identifier
   * @param canonicalId - The identifier to get the canonical route from
   * @returns The canonical route
   */
  getCanonicalRoute(canonicalId: string): string {
    const baseElement = this.document.querySelector('base');
    const seoRoute = this.caasRoutesService.getSeoRouteByIdentifier(
      canonicalId,
      this.router.config
    );
    if (seoRoute && baseElement?.href) {
      return baseElement.href + '' + seoRoute.path;
    }
    return baseElement?.href ?? '';
  }

  /**
   * Update the single meta data tag
   * @param type - The type of the meta data
   * @param name - The name of the meta data
   * @param content - The content of the meta data
   */
  updateMetaData(type: 'name' | 'property', name: string, content: string) {
    this.meta.updateTag({
      [type]: name,
      content: content,
    });
  }
}
